import { DialogModule } from '@angular/cdk/dialog';
import { DecimalPipe, NgClass, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  Signal,
  signal,
  untracked,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { ColorType, createChart, IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import { IEditorContent } from '~global-interfaces/IEditorContent';
import { IResearch } from '~global-interfaces/IResearch';
import { IResearchOverviewItem } from '~global-interfaces/IResearchOverviewItem';
import { AuthService } from '~features/auth/auth.service';
import { BookAMeetingComponent } from '~features/book-a-meeting/book-a-meeting.component';
import { ShareholdersBarchartComponent } from '~features/charts/shareholders-barchart/shareholders-barchart.component';
import { CompanyInfoService } from '~features/company/company-info.service';
import { CompanyRatingService } from '~features/company/company-rating.service';
import { CompanyResearchService } from '~features/company/company-research.service';
import { CompanyTickerService } from '~features/company/company-ticker.service';
import { CompanydataService } from '~features/company/companydata.service';
import { CompanyinfoSliderComponent } from '~features/company/companyinfo-slider/companyinfo-slider.component';
import { CompanylistService } from '~features/company/companylist.service';
import { ExcelService } from '~features/company/excel.service';

import { InvestmentCaseService } from '~features/company/investment-case.service';
import { ShareholderDialogComponent } from '~features/company/shareholder-dialog/shareholder-dialog.component';
import { SharepriceDialogComponent } from '~features/company/shareprice-dialog/shareprice-dialog.component';
import { SwotService } from '~features/company/swot.service';
import { ValuationService } from '~features/company/valuation.service';
import { TileSettingsService } from '~features/dynamic-tiles/tile-settings.service';
import { TileWrapperGridComponent } from '~features/dynamic-tiles/tile-wrapper-grid/tile-wrapper-grid.component';
import { DisplayEditorContentComponent } from '~features/editor/display-editor-content/display-editor-content.component';
import { EventService } from '~features/events/event.service';
import { isEventPast } from '~features/events/event.utils';
import { FrontendConfigService } from '~features/frontend-config/frontendConfig.service';
import { ButtonDownloadPdfComponent } from '~features/pdfAnalytics/button-download-pdf/button-download-pdf.component';
import { GlobalPodcastPlayerService } from '~features/podcasts/global-podcast-player/global-podcast-player.service';
import { PodcastService } from '~features/podcasts/podcast.service';
import { ResearchAccordionComponent } from '~features/research/research-accordion/research-accordion.component';
import { ResearchListDialogComponent } from '~features/research/research-list-dialog/research-list-dialog.component';
import { SectorToastComponent } from '~features/sector-toast/sector-toast.component';
import { SectorToastService } from '~features/sector-toast/sector-toast.service';
import { SEOService } from '~features/seo/seo.service';
import { StructuredDataService } from '~features/seo/structured-data.service';
import { DcfModelComponent } from '~features/tables/dcf-model/dcf-model.component';
import { FinancialsTableComponent } from '~features/tables/financials-table/financials-table.component';
import { KeyfactsComponent } from '~features/tables/keyfacts/keyfacts.component';
import { TableNotabsComponent } from '~features/tables/table-notabs/table-notabs.component';
import { Tables } from '~features/tables/tables.util';
import { SlugService } from '~features/url-slugs/slug.service';
import { TrackButtonClickDirective, TrackDownloadDirective } from '~features/user-behavior-analytics/directives';
import { UserNotificationService } from '~features/user-notifications/user-notification.service';
import { VideoPlayerComponent } from '~features/video/video-player/video-player.component';
import { enableSignalWritesInEffectContext } from '~options/effectOptions';
import { HorizontalNavPanelComponent } from '~shared/components/contentful/horizontal-nav-panel/horizontal-nav-panel.component';
import { MoreContentCtaComponent } from '~shared/components/contentful/more-content-cta/more-content-cta.component';
import { ArrowIconComponent } from '~shared/components/ui/arrow-icon/arrow-icon.component';
import { DateComponent } from '~shared/components/ui/date/date.component';
import { LogoComponent } from '~shared/components/ui/logo/logo.component';
import { TabComponent } from '~shared/components/ui/tab/tab.component';
import { TabsComponent } from '~shared/components/ui/tabs/tabs.component';
import { INITIAL_COMPANY } from '~shared/initial-values';
import { DialogService } from '~shared/services/dialog.service';
import { PlotlyService } from '~shared/services/plotly.service';
import { getNumberColor, getTextColor } from '~utils/coloring.utils';
import { formatTVChartTicker } from '~utils/company.utils';
import { readThemeColor } from '~utils/theme-color.util';
import { IEvent } from '~global-interfaces/IEvent';
import { BRAND } from '~global-constants/brand';

@Component({
  selector: 'app-company-page',
  imports: [
    DisplayEditorContentComponent,
    ResearchAccordionComponent,
    KeyfactsComponent,
    FinancialsTableComponent,
    TableNotabsComponent,
    DcfModelComponent,
    TabsComponent,
    DialogModule,
    TabComponent,
    TileWrapperGridComponent,
    DecimalPipe,
    ShareholdersBarchartComponent,
    HorizontalNavPanelComponent,
    RouterLink,
    CompanyinfoSliderComponent,
    ArrowIconComponent,
    LogoComponent,
    DateComponent,
    VideoPlayerComponent,
    NgClass,
    MoreContentCtaComponent,
    ButtonDownloadPdfComponent,
    NgOptimizedImage,
    SectorToastComponent,
    TrackButtonClickDirective,
    TrackDownloadDirective,
  ],
  templateUrl: './company-page.component.html',
  styleUrl: './company-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyPageComponent implements OnDestroy {
  readonly brand = BRAND;
  @ViewChildren('tvShareChart') chart;

  private destroyRef = inject(DestroyRef);
  public route = inject(ActivatedRoute);
  public dialogService = inject(DialogService);
  private authService = inject(AuthService);

  // Convert route params to signal (modern Angular pattern)
  routeParams = toSignal(this.route.params, {
    initialValue: this.route.snapshot.params,
  });
  companySlug = computed(() => this.routeParams().slug || '');

  // Local Company Data
  companyName: WritableSignal<string> = signal('');
  // next and previous company
  previousCompany = signal(INITIAL_COMPANY);
  nextCompany = signal(INITIAL_COMPANY);
  /**
   * Tradinv view chart
   *
   */
  chartID = String(Math.floor(Math.random() * 100));
  chartWidth = 600;
  chartHeight = 300;
  chartObj: IChartApi | null = null;
  lineSeries: ISeriesApi<'Line'> | null = null;
  public companyInfoSliderIndex: WritableSignal<number> = signal(0);
  structuredDataService = inject(StructuredDataService);
  podcastService = inject(PodcastService);
  globalPodcastPlayerService = inject(GlobalPodcastPlayerService);
  latestPodcast = this.podcastService.getCurrentPodcast();

  // Component-local initiation note state

  chartTileBaseYear = signal(2023);

  tableUtils = inject(Tables);
  valuationService = inject(ValuationService);
  /**
   *  Lifecycles
   *
   **/
  valuationEditorContent: Signal<IEditorContent | null> = this.valuationService.getValuation();
  valuationTradingMultiplesTableTemplate = 'Keyfacts Multiples';
  valuationDCFTableTemplate = 'Default';
  valuationFCFYieldTableTemplate = 'Adj. FCF yield';
  /**
   * End of tradinv view chart
   */
  seoService = inject(SEOService);
  frontendConfigService = inject(FrontendConfigService);
  currentFrontendConfig = this.frontendConfigService.getFrontendConfig();
  slugService = inject(SlugService);
  plotlyLoaded = signal(false);
  plotly = signal<any>(null);
  podcastIsLoading = this.globalPodcastPlayerService.isLoading;
  investorRelationsURL = computed(() => {
    const key = 'ir_website';
    if (this.excelMapping() && this.excelMapping()[key] && this.companyNumbers()) {
      const mappingEntry = this.excelMapping()[key];
      if (mappingEntry.type === 'VALUE' || mappingEntry.type === '') {
        const entry = {
          title: mappingEntry[''],
          value: this.tableUtils.getValue(this.companyNumbers(), mappingEntry, 'eng', 0),
        };
        return entry.value;
      } else {
        return null;
      }
    } else {
      return null;
    }
  });
  weeklyUpDown = computed(() => {
    return this.getChangeInPerc(this.companyHistoricalQuotes(), 5, 'adjusted_close');
  });
  monthlyUpDown = computed(() => {
    return this.getChangeInPerc(this.companyHistoricalQuotes(), 23, 'adjusted_close');
  });
  yearlyUpDown = computed(() => {
    return this.getChangeInPerc(this.companyHistoricalQuotes(), 253, 'adjusted_close');
  });
  chartData = computed(() => {
    const quotes = this.companyHistoricalQuotes();
    const uniqueQuotes = [...quotes.reduce((map, obj) => map.set(obj.date, obj), new Map()).values()];

    //TODO: we need to calculate the currencies corrected values -> see old code

    const newChartData = uniqueQuotes.map((el) => ({
      time: el.date,
      value: el.adjusted_close,
    }));
    newChartData.sort((a, b) => (a.time > b.time ? 1 : 0));
    return newChartData;
  });
  protected readonly encodeURIComponent = encodeURIComponent;
  protected readonly encodeURI = encodeURI;
  protected readonly Object = Object;
  protected readonly getNumberColor = getNumberColor;
  protected readonly getTextColor = getTextColor;
  private companyInfoSliderSlides = 5;
  private eventService = inject(EventService);

  // Component-local company events signal
  companyEvents = signal<IEvent[] | null>(null);

  lastCompanyEvent = computed(() => {
    const lastEvent = this.companyEvents()?.find((event) => isEventPast(event) && Boolean(event.videoURL));
    return lastEvent;
  });

  upcomingCompanyEvent = computed(() => {
    const events = this.companyEvents();
    if (!events || events.length === 0) return null;

    // Filter upcoming events and sort by date (earliest first)
    const upcomingEvents = events
      .filter((event) => !isEventPast(event))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  });

  getRouterLinkPath = computed(() => {
    // Case 1: Already Registered -> Dashboard Path
    if (this.upComingEventAlreadyRegistered()) {
      return ['/user/dashboard'];
    }

    // Case 2: Not Registered -> Registration Path
    const details = this.eventRoutingDetails();
    const event = this.upcomingCompanyEvent();
    const ticker = event?.companyTicker || this.companyTicker();

    if (details) {
      // Returns the path as an array of segments:
      return ['/events/registration', `${details.dateStr}-${details.timeStr}`, ticker];
    }

    // Default fallback path
    return null;
  });

  eventRoutingDetails = computed(() => {
    const event = this.upcomingCompanyEvent();
    if (!event) return null;

    const eventDate = new Date(event.date);

    // Date YYYY-MM-DD
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Time HH-MM
    let timeStr = '00-00';
    if (event.startTime) {
      const timeParts = event.startTime.split(':');
      if (timeParts.length >= 2) {
        timeStr = `${timeParts[0]}-${timeParts[1]}`;
      }
    } else {
      const hours = String(eventDate.getHours()).padStart(2, '0');
      const minutes = String(eventDate.getMinutes()).padStart(2, '0');
      timeStr = `${hours}-${minutes}`;
    }

    return { dateStr, timeStr };
  });

  upcomingEventTime = computed(() => {
    const event = this.upcomingCompanyEvent();
    if (!event) return null;

    // First try startTime
    if (event.startTime) {
      return event.startTime;
    }

    // Extract time from date field
    const eventDate = new Date(event.date);
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  });

  upComingEventAlreadyRegistered = computed(() => {
    const event = this.upcomingCompanyEvent();
    const userMail = this.authService.getCurrentUserEmail();
    if (!event || !userMail) return false;
    if (event.subscribers && event.subscribers.length > 0) {
      return event.subscribers.some((subscriber) => {
        return subscriber.email === userMail;
      });
    }
    return false;
  });

  getRouterLinkParams = computed(() => {
    // Query params only needed for the registered user's path
    if (this.upComingEventAlreadyRegistered()) {
      return { tab: 1 };
    }
    // Otherwise, return an empty object
    return {};
  });

  // Optional: Text for the link
  linkText = computed(() => {
    if (this.upComingEventAlreadyRegistered()) {
      return 'You are registered for';
    }
    return 'Click here to register for';
  });

  private companyInfoService = inject(CompanyInfoService);
  initiationNoteDisplay = this.companyInfoService.getInitiationNoteDisplay();
  private companyListService = inject(CompanylistService);
  companyList = this.companyListService.getCompanyList();
  companyListIsLoaded = this.companyListService.isLoaded;
  private companyDataService = inject(CompanydataService);
  companySettings = this.companyDataService.getCurrentCompanySettings();
  baseyear: Signal<number> = computed(() => {
    return this.companySettings().baseyear;
  });
  private swotService = inject(SwotService);
  swotContent = this.swotService.getSwot();
  // Company Data from Services but in local computed signals
  private investmentCaseService = inject(InvestmentCaseService);
  investmentCaseEditorContent = this.investmentCaseService.getInvestmentCase();
  private excelService = inject(ExcelService);
  valuationTextNumbers = this.excelService.getValuationTextNumbers();
  // "General" Data from Services
  excelMapping = this.excelService.getExcelMapping();
  companyNumbers = this.excelService.getCompanyNumbers();
  companyLiveQuotes = this.excelService.getCompanyLiveQuotes();
  companyHistoricalQuotes = this.excelService.getCompanyHistoricalQuotes();
  private companyTickerService = inject(CompanyTickerService);
  // Company Data from Services
  companyTicker = this.companyTickerService.getCompanyTicker();
  private companyResearchService = inject(CompanyResearchService);

  // Component-local research state (migrated from shared service signals)
  researchOverview = signal<IResearchOverviewItem[]>([]);
  researchArticle = signal<IResearch | null>(null);
  isLoadingResearch = signal(false);
  private copmpanyRatingService = inject(CompanyRatingService);
  private tileSettingsService = inject(TileSettingsService);
  // Tiles
  researchTileSettings = this.tileSettingsService.researchTileSettings;
  keyfiguresTileSettings = this.tileSettingsService.keyfiguresTileSettings;
  swotTileSettings = this.tileSettingsService.swotTileSettings;
  investmentCaseChartSettings = this.tileSettingsService.investmentCaseChartSettings;
  valuationTileSettings = this.tileSettingsService.valuationTileSettings;
  private router = inject(Router);
  private userNotificationService = inject(UserNotificationService);
  private plotlyService = inject(PlotlyService);
  private sectorToastService = inject(SectorToastService);
  chartLoading = signal(true);
  chartInitialized = signal(false);

  private loadPlotly() {
    this.plotlyService
      .loadPlotly()
      .then((plotlyInstance) => {
        this.plotly.set(plotlyInstance);
        this.plotlyLoaded.set(true);
        //this.prepareChart();
      })
      .catch((error) => {
        console.error('Failed to load Plotly:', error);
      });
  }

  constructor() {
    this.loadPlotly();

    // Redirect if slug contains spaces (use effect to react to route changes)
    effect(() => {
      const slug = this.companySlug();
      if (slug) {
        this.redirectToSlugIfNeeded(slug);
      }
    });

    // Effect to handle chart initialization and updates
    effect(() => {
      console.log('Chart effect triggered', {
        hasData: this.companyHistoricalQuotes()?.length > 0,
      });

      if (this.chart?.first && this.companyHistoricalQuotes()?.length > 0) {
        this.chartLoading.set(true);
        this.prepareChart();
        this.chartLoading.set(false);
      }
    }, enableSignalWritesInEffectContext);

    effect(() => {
      // Wait for company list to be loaded before checking for 404
      // This prevents premature 404 redirects when admin users are still authenticating
      if (this.companyListIsLoaded() && this.routeParams().slug) {
        // Check if the slug exists in current company list
        const companyExists = this.companyList().some((company) => company.slug == this.routeParams().slug);

        if (!companyExists) {
          // Before showing 404, check if this might be an old company name/slug that should be redirected
          const frontendConfig = this.currentFrontendConfig();
          let shouldRedirect = false;

          if (frontendConfig && frontendConfig.renamedCompanies) {
            const currentSlug = this.routeParams().slug;

            shouldRedirect = frontendConfig.renamedCompanies.some((company) => {
              const oldSlug = this.slugService.formatSlugFromCompanyName(company.oldName);
              // Make case-insensitive comparisons
              const matches =
                currentSlug.toLowerCase() === oldSlug.toLowerCase() ||
                currentSlug.toLowerCase() === company.oldName.toLowerCase();
              return matches;
            });
          }

          // Only show 404 if this is not a redirect case
          if (!shouldRedirect) {
            this.router.navigate([''], {
              queryParams: { '404': this.routeParams().slug },
            });
          }
        }
      }
    });
    // Effect 1: Load research data when company slug changes
    // This effect ONLY watches companySlug and loads data
    // Uses untracked() to avoid creating unnecessary dependencies
    effect(() => {
      const slug = this.companySlug();
      if (this.companyListIsLoaded() && slug) {
        const companyTicker = this.companyListService.getCompanyTickerFromSlug(slug);
        const companyName = this.companyListService.getCompanyNameFromSlug(slug);

        // Set global state (these don't trigger this effect)
        untracked(() => {
          this.companyTickerService.setCompanyTicker(companyTicker);
          this.companyName.set(companyName);
        });

        // Load research data for this company
        this.loadResearchData(companyTicker);

        // Load company events
        this.loadCompanyEvents(companyTicker);

        // Load initiation note data (handles cascading load to research)
        this.companyInfoService.fetchInitiationNoteDisplay(companyTicker);
      }
    });

    // Effect 2: Update SEO when research data is loaded
    // This effect watches researchArticle to update SEO metadata
    // Separated from data loading to prevent infinite loops
    effect(() => {
      const research = this.researchArticle();
      if (research?.companyTicker) {
        // Use untracked to read other signals without creating dependencies
        untracked(() => {
          this.structuredDataService.addStructuredData(this.companyName(), research, this.companySettings().imageURL);
        });
      }
    });

    // Effect 3: Update SEO meta tags when company changes
    effect(() => {
      const slug = this.companySlug();
      if (this.companyListIsLoaded() && slug) {
        const companyTicker = this.companyListService.getCompanyTickerFromSlug(slug);
        const companyName = this.companyListService.getCompanyNameFromSlug(slug);

        this.seoService.createAndSetCompanyPageMetaTags(companyName, companyTicker, this.router.url);
      }
    });

    effect(() => {
      if (this.companyListIsLoaded()) {
        this.previousCompany.set(this.getAdjacentCompany(-1));
        this.nextCompany.set(this.getAdjacentCompany(1));
      }
      if (this.chart && this.companyHistoricalQuotes()?.length > 0) {
        this.updateChart();
      }
    }, enableSignalWritesInEffectContext);

    effect(() => {
      if (this.companySettings()?.sector) {
        this.showSectorToast(this.companySettings()?.sector, this.companySettings().companyTicker);
      }
    }, enableSignalWritesInEffectContext);

    effect(() => {
      if (this.chart && this.companyHistoricalQuotes()?.length > 0) {
        this.prepareChart();
      }
    }, enableSignalWritesInEffectContext);

    effect(() => {
      if (this.currentFrontendConfig() && this.routeParams().slug) {
        this.checkIfRedirectionIsNeededThenRedirect(this.currentFrontendConfig());
      }
    }, enableSignalWritesInEffectContext);
    //this.sectorToastService.show();
  }

  /**
   * Load research data for the current company (migrated from auto-loading effect)
   * This replaces the service's auto-loading effect with component-local state management
   */
  private loadResearchData(companyTicker: string) {
    if (!companyTicker) return;

    this.isLoadingResearch.set(true);

    // Load latest research article
    this.companyResearchService
      .getResearchByTitle$(companyTicker, 'latest')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (research) => {
          this.researchArticle.set(research ?? null);
        },
        error: (err) => {
          console.error('Failed to load research article:', err);
          this.researchArticle.set(null);
        },
      });

    // Load research overview list
    this.companyResearchService
      .getResearchOverview$(companyTicker, { page: 1, limit: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ items }) => {
          this.researchOverview.set(items);
          this.isLoadingResearch.set(false);
        },
        error: (err) => {
          console.error('Failed to load research overview:', err);
          this.researchOverview.set([]);
          this.isLoadingResearch.set(false);
        },
      });
  }

  /**
   * Load company events for the current company
   * Replaces EventService auto-loading effect with component-local state
   */
  private loadCompanyEvents(companyTicker: string) {
    if (!companyTicker) return;

    this.eventService
      .getCompanyEvents$(companyTicker)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (events) => {
          this.companyEvents.set(events);
        },
        error: (err) => {
          console.error('Failed to load company events:', err);
          this.companyEvents.set([]);
        },
      });
  }

  playPodacst() {
    this.globalPodcastPlayerService.playPodcast(this.companyTicker(), this.researchArticle()?.research_date, 'en');
  }

  showSectorToast(sector: string, companyTicker: string) {
    this.sectorToastService.update(sector, companyTicker);
  }
  // ngAfterViewInit() {
  //   this.prepareChart();
  //   this.updateChart();
  // }
  redirectToSlugIfNeeded(textFromURLParam: string) {
    if (textFromURLParam.includes(' ') || textFromURLParam.includes('%20')) {
      this.router.navigate(['/companies', this.slugService.formatSlugFromCompanyName(textFromURLParam)]);
    }
  }

  checkIfRedirectionIsNeededThenRedirect(frontendConfig) {
    if (frontendConfig && frontendConfig.renamedCompanies) {
      const currentSlug = this.routeParams().slug;

      const renamedCompany = frontendConfig.renamedCompanies.find((company) => {
        // Generate the old slug from the old company name
        const oldSlug = this.slugService.formatSlugFromCompanyName(company.oldName);
        // Generate the new slug from the new company name
        const newSlug = this.slugService.formatSlugFromCompanyName(company.newName);

        // IMPORTANT: Only redirect if current slug matches OLD data, NOT new data
        // Make case-insensitive comparisons
        const matchesOldSlug = currentSlug.toLowerCase() === oldSlug.toLowerCase();
        const matchesOldName = currentSlug.toLowerCase() === company.oldName.toLowerCase();
        const isNotNewSlug = currentSlug.toLowerCase() !== newSlug.toLowerCase();

        return (matchesOldSlug || matchesOldName) && isNotNewSlug;
      });

      if (renamedCompany) {
        const newSlug = this.slugService.formatSlugFromCompanyName(renamedCompany.newName);
        this.router.navigate(['/companies', newSlug]);
      }
    }
  }

  companyInfoSliderNextSlide() {
    this.companyInfoSliderIndex.update(
      (index) => (index + 1 + this.companyInfoSliderSlides) % this.companyInfoSliderSlides
    );
  }

  companyInfoSliderPrevSlide() {
    this.companyInfoSliderIndex.update(
      (index) => (index - 1 + this.companyInfoSliderSlides) % this.companyInfoSliderSlides
    );
  }

  updateChart() {
    console.log('updateChart called', {
      dataLength: this.chartData()?.length,
    });

    if (this.chartObj) {
      try {
        // Remove existing series
        if (this.lineSeries) {
          this.chartObj.removeSeries(this.lineSeries);
          this.lineSeries = null;
        }

        // Create new series
        this.lineSeries = this.chartObj.addLineSeries({
          color: readThemeColor('--color-brand-500', '#0f6bb2'),
          lineWidth: 3,
          lastValueVisible: false,
          priceLineVisible: false,
          baseLineVisible: false,
        });

        // Set new data
        if (this.chartData()?.length > 0) {
          this.lineSeries.setData(this.chartData());
          this.setTimeScale();
        }
      } catch (error) {
        console.error('Error updating chart:', error);
      }
    }
  }

  setTimeScale() {
    const startDate = new Date(Date.now());
    startDate.setFullYear(startDate.getFullYear() - 1);
    const endDate = new Date(Date.now());

    this.chartObj?.timeScale().setVisibleRange({
      from: (startDate.getTime() / 1000) as UTCTimestamp,
      to: (endDate.getTime() / 1000) as UTCTimestamp,
    });
  }

  prepareChart() {
    // Only create new chart if it doesn't exist
    if (!this.chartObj && this.chart?.first) {
      const rect = this.chart.first.nativeElement.getBoundingClientRect();

      this.chartHeight = rect.height > 0 ? rect.height : 150;
      this.chartWidth = rect.width * 0.9;

      try {
        this.chartObj = createChart('tvShareChart-' + this.chartID, {
          crosshair: {},
          handleScroll: {
            mouseWheel: false,
            pressedMouseMove: false,
            horzTouchDrag: false,
            vertTouchDrag: false,
          },
          handleScale: {
            mouseWheel: false,
            pinch: false,
            axisPressedMouseMove: false,
          },
          layout: {
            background: { color: readThemeColor('--surface-tile', '#8ba1be'), type: ColorType.Solid },
            attributionLogo: false,
          },
          watermark: {
            visible: false,
          },
          width: this.chartWidth,
          height: this.chartHeight,
          rightPriceScale: {
            visible: false,
          },
          leftPriceScale: {
            visible: true,
            autoScale: true,
          },
          timeScale: {
            visible: true,
            borderVisible: true,
            fixLeftEdge: true,
            fixRightEdge: true,
            lockVisibleTimeRangeOnResize: true,
          },
          grid: {
            horzLines: {
              visible: false,
            },
            vertLines: {
              visible: false,
            },
          },
        });
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    }

    // Always update chart when preparing
    this.updateChart();
  }

  openBookAMeetingComponent() {
    this.dialogService.open(
      BookAMeetingComponent,
      {
        maxWidth: '80%',
        data: {
          companyTicker: this.companyTicker,
          companySettings: this.companySettings,
        },
      },
      'dialog-panel-tile'
    );
  }

  openResearchList() {
    this.dialogService.open(ResearchListDialogComponent, this.researchOverview, 'dialog-panel-tile');
  }

  openSharePrice() {
    this.dialogService.open(
      SharepriceDialogComponent,
      {
        maxWidth: '80%',
        data: {
          mappedTVChartTicker: this.companySettings()?.mappedTVChartTicker
            ? this.companySettings()?.mappedTVChartTicker
            : formatTVChartTicker(this.companySettings()?.companyTicker),
        },
      },
      'dialog-panel-tile'
    );
  }

  openShareHolders() {
    this.dialogService.open(
      ShareholderDialogComponent,
      {
        maxWidth: '80%',
        data: {
          companyNumbers: this.companyNumbers,
          baseyear: this.baseyear,
        },
      },
      'dialog-panel-tile'
    );
  }

  getChangeInPerc(data: { adjusted_close: number; close: number }[], offset: number, field: string) {
    if (data.length > offset + 1) {
      return (
        ((data[data.length - 1][field] - data[data.length - offset - 1][field]) /
          data[data.length - offset - 1][field]) *
        100
      );
    } else {
      return null;
    }
  }

  ngOnDestroy(): void {
    if (this.chartObj) {
      this.chartObj.remove();
      this.chartObj = null;
    }
    this.structuredDataService.removeStructuredData();
  }

  private getAdjacentCompany(offset: number) {
    if (this.companyList()) {
      const currentIndex = this.companyList().findIndex((company) => company.companyTicker === this.companyTicker());

      const adjacentIndex = (currentIndex + offset + this.companyList().length) % this.companyList().length;

      return this.companyList()[adjacentIndex];
    } else {
      return null;
    }
  }
}
